import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/sportsData';
import type { Article } from '@/mocks/sportsData';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  football: {
    title: '足球资讯 - 九游体育 | 足球赛事直播、新闻与分析',
    description: '九游体育足球频道为您提供全球足球赛事直播、最新足球新闻、深度战术分析和实时比分更新。涵盖英超、西甲、欧冠等顶级联赛资讯。',
    keywords: '九游体育足球,足球赛事直播,足球新闻,足球分析,英超,西甲,欧冠',
  },
  basketball: {
    title: '篮球资讯 - 九游体育 | 篮球赛事直播、NBA新闻与分析',
    description: '九游体育篮球频道为您提供NBA、CBA等顶级篮球赛事直播、最新篮球新闻、球员数据分析和实时比分。',
    keywords: '九游体育篮球,篮球赛事直播,NBA新闻,CBA新闻,篮球分析',
  },
  tennis: {
    title: '网球资讯 - 九游体育 | 网球赛事直播、新闻与排名',
    description: '九游体育网球频道为您提供ATP、WTA等顶级网球赛事直播、最新网球新闻、球员排名和比赛分析。',
    keywords: '九游体育网球,网球赛事直播,网球新闻,ATP,WTA,大满贯',
  },
  esports: {
    title: '电竞资讯 - 九游体育 | 电子竞技赛事直播、新闻与分析',
    description: '九游体育电竞频道为您提供英雄联盟、DOTA2等顶级电竞赛事直播、最新电竞新闻和深度比赛分析。',
    keywords: '九游体育电竞,电竞直播,英雄联盟,DOTA2,LPL,电竞新闻',
  },
  general: {
    title: '综合体育 - 九游体育 | 综合体育赛事资讯与分析',
    description: '九游体育综合频道为您提供奥运会、田径、游泳等综合体育项目的最新资讯、赛事直播和深度分析。',
    keywords: '九游体育综合,综合体育,奥运会,田径,游泳,体育新闻',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>(slug || 'football');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    const filtered = articles.filter((a) => a.categorySlug === activeCategory);
    setFilteredArticles(filtered);
  }, [activeCategory]);

  const currentCat = categories.find((c) => c.slug === activeCategory);
  const meta = categoryMeta[activeCategory] || categoryMeta.football;

  const handleCategoryChange = useCallback((newSlug: string) => {
    setActiveCategory(newSlug);
    window.REACT_APP_NAVIGATE(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, []);

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://www.jiuyou-sports.com/category/${activeCategory}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-800 font-medium">{currentCat?.name}</span>
              </nav>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCat?.name}资讯
              </h1>
              <p className="mt-2 text-sm text-foreground-500">{meta.description.split('。')[0]}。</p>
            </div>
          </div>

          <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8">
            <div className="flex items-center gap-2 flex-wrap mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-semibold text-sm md:text-base text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-600 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <span className="text-xs text-foreground-500">{article.publishDate}</span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.readCount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">暂无该分类的文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}